import React, { createContext, useContext, useState, useEffect } from "react";
import { useMemberDetails } from "./MemberDetailsContext";
import useIsMobile from "components/hooks/useIsMobile";
import { getPreviewStatus, DIGITAL_PREVIEW_DAYS } from "utils/previewStatus";
import { FamilyStatus } from "protogen/advisors_service_pb";
import { DIGITAL_SUBSCRIPTION_TRIAL_EXEMPT_FEATURE_FLAG } from "components/helpers/GatedComponent";

interface UnpaidBannerContextType {
  shouldShowBanner: boolean;
  daysLeft: number;
  isUnpaid: boolean;
  shouldShowDigitalUpgradeBanner: boolean;
  digitalDaysLeft: number;
  loading: boolean;
}

const UnpaidBannerContext = createContext<UnpaidBannerContextType>({
  shouldShowBanner: false,
  daysLeft: 0,
  isUnpaid: false,
  shouldShowDigitalUpgradeBanner: false,
  digitalDaysLeft: 0,
  loading: true,
});

export const useUnpaidBanner = () => useContext(UnpaidBannerContext);

const getDigitalDaysLeft = (createdAtSec: bigint): number => {
  const createdDate = new Date(Number(createdAtSec) * 1000);
  const diffDays = Math.floor(
    (Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24),
  );
  return Math.max(0, DIGITAL_PREVIEW_DAYS - diffDays);
};

export const UnpaidBannerProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const isMobile = useIsMobile();
  const { data, loading } = useMemberDetails();
  const [value, setValue] = useState<UnpaidBannerContextType>({
    shouldShowBanner: false,
    daysLeft: 0,
    isUnpaid: false,
    shouldShowDigitalUpgradeBanner: false,
    digitalDaysLeft: 0,
    loading: true,
  });

  useEffect(() => {
    if (loading || !data || !data.family) {
      setValue({
        shouldShowBanner: false,
        daysLeft: 0,
        isUnpaid: false,
        shouldShowDigitalUpgradeBanner: false,
        digitalDaysLeft: 0,
        loading,
      });
      return;
    }

    const previewStatus = getPreviewStatus(data.family);
    const isDigitalTrialExempt =
      data.member?.enabledFeatureFlags.includes(
        DIGITAL_SUBSCRIPTION_TRIAL_EXEMPT_FEATURE_FLAG,
      ) ?? false;
    const isDigitalNeedingUpgrade =
      data.family.status === FamilyStatus.DIGITAL_SUBSCRIPTION &&
      !data.family.billingInfo?.product &&
      !isDigitalTrialExempt;
    const digitalDaysLeft = isDigitalNeedingUpgrade
      ? getDigitalDaysLeft(data.family.createdAtSec)
      : 0;

    // NOTE(Kip): I am struggling to get this to work on mobile, hiding for now.
    if (isMobile || !previewStatus.isUnpaid) {
      setValue({
        shouldShowBanner: false,
        daysLeft: 0,
        isUnpaid: previewStatus.isUnpaid,
        shouldShowDigitalUpgradeBanner: isDigitalNeedingUpgrade,
        digitalDaysLeft,
        loading: false,
      });
      return;
    }

    setValue({
      shouldShowBanner: true,
      daysLeft: previewStatus.daysLeft,
      isUnpaid: true,
      shouldShowDigitalUpgradeBanner: isDigitalNeedingUpgrade,
      digitalDaysLeft,
      loading: false,
    });
  }, [data, loading]);

  return (
    <UnpaidBannerContext.Provider value={value}>
      {children}
    </UnpaidBannerContext.Provider>
  );
};
